import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';

// Sound effects using Web Audio API
const createAudioContext = () => {
  if (typeof window !== 'undefined') {
    return new (window.AudioContext || window.webkitAudioContext)();
  }
  return null;
};

const playSound = (audioContext, type) => {
  if (!audioContext) return;
  
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  switch (type) {
    case 'hit':
      oscillator.frequency.setValueAtTime(200, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(50, audioContext.currentTime + 0.2);
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
      break;
    case 'score':
      oscillator.frequency.setValueAtTime(523, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(659, audioContext.currentTime + 0.1);
      oscillator.frequency.setValueAtTime(784, audioContext.currentTime + 0.2);
      gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
      break;
    case 'levelup':
      oscillator.type = 'square';
      oscillator.frequency.setValueAtTime(440, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(554, audioContext.currentTime + 0.1);
      oscillator.frequency.setValueAtTime(659, audioContext.currentTime + 0.2);
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime + 0.3);
      gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
      break;
    case 'crash':
      oscillator.type = 'sawtooth';
      oscillator.frequency.setValueAtTime(150, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(30, audioContext.currentTime + 0.3);
      gainNode.gain.setValueAtTime(0.4, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
      break;
    case 'powerup':
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(300, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(1200, audioContext.currentTime + 0.2);
      gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
      break;
    default:
      break;
  }
};

const LEVEL_CONFIG = {
  1: { name: "First Day on the Job", threshold: 30, color: "#00ff88" },
  2: { name: "Taxi Trouble", threshold: 50, color: "#ffcc00" },
  3: { name: "Rush Hour", threshold: 80, color: "#ff8800" },
  4: { name: "Construction Zone", threshold: 120, color: "#ff6600" },
  5: { name: "Night Shift", threshold: 170, color: "#6644ff" },
  6: { name: "Highway Patrol", threshold: 230, color: "#00aaff" },
  7: { name: "VIP Escort", threshold: 300, color: "#ffdd00" },
  8: { name: "Weather Alert", threshold: 380, color: "#8844ff" },
  9: { name: "Special Forces", threshold: 470, color: "#ff4444" },
  10: { name: "Final Showdown", threshold: 600, color: "#ff0066" }
};

const POWER_UPS = {
  shield: { emoji: '🛡️', duration: 5000, color: '#00ffff' },
  speed: { emoji: '⚡', duration: 4000, color: '#ffff00' },
  timeFreeze: { emoji: '❄️', duration: 3000, color: '#88ddff' },
  magnet: { emoji: '🧲', duration: 6000, color: '#ff00ff' },
  doublePoints: { emoji: '✨', duration: 8000, color: '#ffd700' }
};

function App() {
  const [lastCopCarTime, setLastCopCarTime] = useState(0);
  const [gameState, setGameState] = useState('start');
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [timeLeft, setTimeLeft] = useState(15);
  const [totalPlayTime, setTotalPlayTime] = useState(0);
  const [scoreNotification, setScoreNotification] = useState(null);
  const [timeNotification, setTimeNotification] = useState(null);
  const [levelUpNotification, setLevelUpNotification] = useState(false);
  const [carPosition, setCarPosition] = useState({ x: 50, y: 90 });
  const [carVelocity, setCarVelocity] = useState({ x: 0, y: 0 });
  const [vehicles, setVehicles] = useState([]);
  const [explosions, setExplosions] = useState([]);
  const [carDirection, setCarDirection] = useState('right');
  const [conePosition, setConePosition] = useState({ y: -15, side: 'left' });
  const [spinDirection, setSpinDirection] = useState(null);
  const [weatherEffect, setWeatherEffect] = useState(null);
  const [darknessLevel, setDarknessLevel] = useState(0);
  const [isRoadSlippery, setIsRoadSlippery] = useState(false);
  const [constructionActive, setConstructionActive] = useState(false);
  const [vipActive, setVipActive] = useState(false);
  const [highScores, setHighScores] = useState([]);
  const [activePowerUp, setActivePowerUp] = useState(null);
  const [powerUps, setPowerUps] = useState([]);
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const gameContainerRef = useRef(null);
  const audioContextRef = useRef(null);
  const [settings, setSettings] = useState({
    controlType: 'touch',
    sensitivity: 50,
    responsiveness: 50
  });
  const [showSettings, setShowSettings] = useState(false);

  // Load high scores from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('roadrage_highscores');
    if (saved) {
      setHighScores(JSON.parse(saved));
    }
  }, []);

  // Initialize audio context on first interaction
  useEffect(() => {
    const initAudio = () => {
      if (!audioContextRef.current) {
        audioContextRef.current = createAudioContext();
      }
    };
    
    window.addEventListener('click', initAudio, { once: true });
    window.addEventListener('touchstart', initAudio, { once: true });
    
    return () => {
      window.removeEventListener('click', initAudio);
      window.removeEventListener('touchstart', initAudio);
    };
  }, []);

  const playSoundEffect = useCallback((type) => {
    if (soundEnabled && audioContextRef.current) {
      playSound(audioContextRef.current, type);
    }
  }, [soundEnabled]);

  const saveHighScore = useCallback((newScore) => {
    const newHighScores = [...highScores, { score: newScore, date: new Date().toLocaleDateString() }]
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
    setHighScores(newHighScores);
    localStorage.setItem('roadrage_highscores', JSON.stringify(newHighScores));
  }, [highScores]);

  const createVehicle = useCallback((type) => {
    let vehicle = {
      type,
      exploded: false,
      collided: false,
      id: Date.now() + Math.random()
    };
    
    switch(type) {
      case 'truck':
        vehicle.x = Math.random() * 100;
        vehicle.y = -10;
        vehicle.speedY = (Math.random() * 0.5 + 0.5) * 3;
        vehicle.speedX = 0;
        vehicle.points = -20;
        vehicle.timeEffect = -10;
        vehicle.size = 3.2;
        break;
        
      case 'police':
        const side = Math.random() < 0.5 ? 'left' : 'right';
        vehicle.x = side === 'left' ? -10 : 110;
        vehicle.y = Math.random() * 100;
        vehicle.speedX = (side === 'left' ? 1 : -1) * (Math.random() * 0.5 + 0.5);
        vehicle.speedY = (Math.random() - 0.5) * 0.5;
        vehicle.points = 10;
        vehicle.timeEffect = 3;
        vehicle.size = 2.75;
        break;
        
      case 'taxi':
        const taxiSide = Math.random() < 0.5 ? 'left' : 'right';
        vehicle.x = taxiSide === 'left' ? -10 : 110;
        vehicle.y = Math.random() * 100;
        vehicle.speedX = (taxiSide === 'left' ? 1 : -1) * (Math.random() * 0.5 + 0.5);
        vehicle.speedY = (Math.random() - 0.5) * 0.5;
        vehicle.points = -10;
        vehicle.timeEffect = -3;
        vehicle.size = 2.75;
        break;
        
      case 'car':
        const carSide = Math.random() < 0.5 ? 'left' : 'right';
        vehicle.x = carSide === 'left' ? -10 : 110;
        vehicle.y = Math.random() * 100;
        vehicle.speedX = (carSide === 'left' ? 1 : -1) * (Math.random() * 0.4 + 0.3);
        vehicle.speedY = (Math.random() - 0.5) * 0.4;
        vehicle.points = -5;
        vehicle.timeEffect = -2;
        vehicle.size = 2.75;
        break;
        
      case 'construction':
        vehicle.x = Math.random() * 100;
        vehicle.y = -10;
        vehicle.speedY = (Math.random() * 0.3 + 0.2) * 2;
        vehicle.speedX = 0;
        vehicle.points = -15;
        vehicle.timeEffect = -5;
        vehicle.size = 3.0;
        break;
        
      case 'ambulance':
        const ambSide = Math.random() < 0.5 ? 'left' : 'right';
        vehicle.x = ambSide === 'left' ? -10 : 110;
        vehicle.y = Math.random() * 100;
        vehicle.speedX = (ambSide === 'left' ? 1 : -1) * (Math.random() * 0.7 + 0.6);
        vehicle.speedY = (Math.random() - 0.5) * 0.3;
        vehicle.points = -25;
        vehicle.timeEffect = -5;
        vehicle.size = 3.0;
        vehicle.flashing = true;
        break;
        
      case 'highway':
        const hwSide = Math.random() < 0.5 ? 'left' : 'right';
        vehicle.x = hwSide === 'left' ? -10 : 110;
        vehicle.y = Math.random() * 100;
        vehicle.speedX = (hwSide === 'left' ? 1 : -1) * (Math.random() * 0.8 + 0.7);
        vehicle.speedY = (Math.random() - 0.5) * 0.3;
        vehicle.points = 15;
        vehicle.timeEffect = 4;
        vehicle.size = 2.8;
        break;
        
      case 'vip':
        const vipSide = Math.random() < 0.5 ? 'left' : 'right';
        vehicle.x = vipSide === 'left' ? -10 : 110;
        vehicle.y = Math.random() * 100;
        vehicle.speedX = (vipSide === 'left' ? 1 : -1) * (Math.random() * 0.6 + 0.5);
        vehicle.speedY = (Math.random() - 0.5) * 0.3;
        vehicle.points = -50;
        vehicle.timeEffect = -8;
        vehicle.size = 3.5;
        vehicle.isVIP = true;
        break;
        
      case 'swat':
        const swatSide = Math.random() < 0.5 ? 'left' : 'right';
        vehicle.x = swatSide === 'left' ? -10 : 110;
        vehicle.y = Math.random() * 100;
        vehicle.speedX = (swatSide === 'left' ? 1 : -1) * (Math.random() * 0.9 + 0.5);
        vehicle.speedY = (Math.random() - 0.5) * 0.6;
        vehicle.points = 25;
        vehicle.timeEffect = 5;
        vehicle.size = 3.2;
        break;
        
      case 'boss':
        vehicle.x = 50;
        vehicle.y = -15;
        vehicle.speedY = 0.5;
        vehicle.speedX = Math.sin(Date.now() / 1000) * 0.5;
        vehicle.points = 50;
        vehicle.timeEffect = 10;
        vehicle.size = 4;
        vehicle.isBoss = true;
        vehicle.health = 3;
        break;
        
      default:
        const defaultSide = Math.random() < 0.5 ? 'left' : 'right';
        vehicle.x = defaultSide === 'left' ? -10 : 110;
        vehicle.y = Math.random() * 100;
        vehicle.speedX = (defaultSide === 'left' ? 1 : -1) * (Math.random() * 0.5 + 0.3);
        vehicle.speedY = (Math.random() - 0.5) * 0.3;
        vehicle.points = -5;
        vehicle.timeEffect = -1;
        vehicle.size = 2.75;
    }
    
    return vehicle;
  }, []);

  const createPowerUp = useCallback(() => {
    const types = Object.keys(POWER_UPS);
    const type = types[Math.floor(Math.random() * types.length)];
    return {
      id: Date.now() + Math.random(),
      type,
      x: Math.random() * 80 + 10,
      y: -5,
      speedY: 0.8
    };
  }, []);

  const applyLevelEffects = useCallback((newLevel) => {
    switch(newLevel) {
      case 4:
        setConstructionActive(true);
        break;
      case 5:
        setDarknessLevel(0.6);
        break;
      case 6:
        break;
      case 7:
        setVipActive(true);
        break;
      case 8:
        setWeatherEffect('rain');
        setIsRoadSlippery(true);
        break;
      case 9:
        break;
      case 10:
        setWeatherEffect('storm');
        setDarknessLevel(0.4);
        setIsRoadSlippery(true);
        break;
      default:
        break;
    }
  }, []);

  const checkLevelUp = useCallback((newScore) => {
    for (let i = 10; i > level; i--) {
      if (newScore >= LEVEL_CONFIG[i].threshold && level === i - 1) {
        setLevel(i);
        setLevelUpNotification(true);
        playSoundEffect('levelup');
        setTimeout(() => setLevelUpNotification(false), 3000);
        applyLevelEffects(i);
        break;
      }
    }
  }, [level, applyLevelEffects, playSoundEffect]);

  const checkCollisions = useCallback((currentCarPosition) => {
    const carRect = {
      left: currentCarPosition.x - 2.5,
      right: currentCarPosition.x + 2.5,
      top: currentCarPosition.y - 2.5,
      bottom: currentCarPosition.y + 2.5,
    };

    // Check power-up collisions
    setPowerUps(prevPowerUps => {
      const remaining = [];
      prevPowerUps.forEach(powerUp => {
        const powerUpRect = {
          left: powerUp.x - 3,
          right: powerUp.x + 3,
          top: powerUp.y - 3,
          bottom: powerUp.y + 3,
        };
        
        if (
          carRect.left < powerUpRect.right &&
          carRect.right > powerUpRect.left &&
          carRect.top < powerUpRect.bottom &&
          carRect.bottom > powerUpRect.top
        ) {
          // Collect power-up
          playSoundEffect('powerup');
          setActivePowerUp({ type: powerUp.type, expiresAt: Date.now() + POWER_UPS[powerUp.type].duration });
        } else if (powerUp.y < 110) {
          remaining.push(powerUp);
        }
      });
      return remaining;
    });

    const coneGroupRect = {
      left: conePosition.side === 'left' ? 2 : 68,
      right: conePosition.side === 'left' ? 32 : 98,
      top: conePosition.y,
      bottom: conePosition.y + 15,
    };

    const potholeRect = {
      left: conePosition.side === 'left' ? 31 : 97,
      right: conePosition.side === 'left' ? 36 : 102,
      top: conePosition.y,
      bottom: conePosition.y + 5,
    };

    // Shield protects from cone/pothole damage
    const hasShield = activePowerUp?.type === 'shield' && Date.now() < activePowerUp.expiresAt;

    if (!hasShield && (
      (carRect.left < coneGroupRect.right &&
      carRect.right > coneGroupRect.left &&
      carRect.top < coneGroupRect.bottom &&
      carRect.bottom > coneGroupRect.top) ||
      (carRect.left < potholeRect.right &&
      carRect.right > potholeRect.left &&
      carRect.top < potholeRect.bottom &&
      carRect.bottom > potholeRect.top)
    )) {
      if (!spinDirection) {
        const newDirection = Math.random() < 0.5 ? 'clockwise' : 'counterclockwise';
        setSpinDirection(newDirection);
        playSoundEffect('crash');
        setCombo(0);
        setScore(prev => {
          setScoreNotification('-10');
          setTimeout(() => setScoreNotification(null), 2000);
          return Math.max(0, prev - 10);
        });
        setTimeLeft(prev => {
          setTimeNotification('-3');
          setTimeout(() => setTimeNotification(null), 2000);
          return Math.max(0, prev - 3);
        });
        setTimeout(() => setSpinDirection(null), 2000);

        const collisionX = (carRect.left + carRect.right) / 2;
        const collisionY = Math.min(carRect.top, coneGroupRect.top, potholeRect.top) - 5;
        setExplosions(prev => [...prev, {
          x: collisionX,
          y: collisionY,
          age: 0,
          type: 'collision'
        }]);
      }
    }

    setVehicles(prevVehicles => prevVehicles.map(vehicle => {
      if (vehicle.exploded) return vehicle;
      
      const vehicleSize = vehicle.size || 2.75;
      
      const vehicleRect = {
        left: vehicle.x - vehicleSize,
        right: vehicle.x + vehicleSize,
        top: vehicle.y - vehicleSize,
        bottom: vehicle.y + vehicleSize,
      };
      
      if (
        carRect.left < vehicleRect.right &&
        carRect.right > vehicleRect.left &&
        carRect.top < vehicleRect.bottom &&
        carRect.bottom > vehicleRect.top
      ) {
        const hasDoublePoints = activePowerUp?.type === 'doublePoints' && Date.now() < activePowerUp.expiresAt;
        const pointMultiplier = hasDoublePoints ? 2 : 1;
        
        if (vehicle.type === 'police' || vehicle.type === 'highway' || vehicle.type === 'swat' || vehicle.type === 'boss') {
          const collisionX = (currentCarPosition.x + vehicle.x) / 2;
          const collisionY = (currentCarPosition.y + vehicle.y) / 2;

          playSoundEffect('score');
          
          // Increase combo
          setCombo(prev => {
            const newCombo = prev + 1;
            setMaxCombo(max => Math.max(max, newCombo));
            return newCombo;
          });

          setExplosions(prev => [...prev, { 
            x: collisionX, 
            y: collisionY, 
            age: 0,
            points: vehicle.points * pointMultiplier,
            time: vehicle.timeEffect
          }]);
          
          if (vehicle.isBoss && vehicle.health > 1) {
            return { ...vehicle, health: vehicle.health - 1 };
          }
          
          setScore(prev => {
            const comboBonus = Math.floor(combo / 3) * 5;
            const newScore = prev + (vehicle.points * pointMultiplier) + comboBonus;
            setScoreNotification(`+${vehicle.points * pointMultiplier}${comboBonus > 0 ? ` (+${comboBonus} combo)` : ''}`);
            setTimeout(() => setScoreNotification(null), 2000);
            checkLevelUp(newScore);
            return newScore;
          });
          
          setTimeLeft(prev => {
            setTimeNotification(`+${vehicle.timeEffect}`);
            setTimeout(() => setTimeNotification(null), 2000);
            return prev + vehicle.timeEffect;
          });
          
          return { ...vehicle, exploded: true };
        } else {
          if (!vehicle.collided) {
            // Shield blocks damage from civilian vehicles
            if (hasShield) {
              return vehicle;
            }
            
            const collisionX = (currentCarPosition.x + vehicle.x) / 2;
            const collisionY = (currentCarPosition.y + vehicle.y) / 2;

            playSoundEffect('crash');
            setCombo(0);

            setExplosions(prev => [...prev, { 
              x: collisionX, 
              y: collisionY, 
              age: 0, 
              type: 'collision',
              points: vehicle.points,
              time: vehicle.timeEffect
            }]);
            
            const shakeIntensity = vehicle.type === 'truck' || vehicle.isVIP ? 'heavy-shake' : 'shake';
            
            setScore(prev => {
              setScoreNotification(`${vehicle.points}`);
              setTimeout(() => setScoreNotification(null), 2000);
              return Math.max(0, prev + vehicle.points);
            });
            
            setTimeLeft(prev => {
              setTimeNotification(`${vehicle.timeEffect}`);
              setTimeout(() => setTimeNotification(null), 2000);
              return Math.max(0, prev + vehicle.timeEffect);
            });
            
            if (gameContainerRef.current) {
              gameContainerRef.current.classList.add(shakeIntensity);
              setTimeout(() => gameContainerRef.current?.classList.remove(shakeIntensity), 500);
            }
            
            return { ...vehicle, collided: true };
          }
          return vehicle;
        }
      }
      return vehicle;
    }));
  }, [conePosition.y, conePosition.side, spinDirection, activePowerUp, combo, playSoundEffect, checkLevelUp]);

  const startGame = () => {
    setGameState('playing');
    setScore(0);
    setLevel(1);
    setTimeLeft(15);
    setVehicles([]);
    setExplosions([]);
    setPowerUps([]);
    setCarPosition({ x: 50, y: 90 });
    setCarVelocity({ x: 0, y: 0 });
    setTotalPlayTime(0);
    setWeatherEffect(null);
    setDarknessLevel(0);
    setIsRoadSlippery(false);
    setConstructionActive(false);
    setVipActive(false);
    setActivePowerUp(null);
    setCombo(0);
    setMaxCombo(0);
  };

  const endGame = useCallback(() => {
    setGameState('over');
    saveHighScore(score);
  }, [score, saveHighScore]);

  useEffect(() => {
    if (gameState !== 'playing') return;

    let animationFrameId;

    const gameLoop = (currentTime) => {

      checkCollisions(carPosition);

      // Update power-ups
      setPowerUps(prev => prev.map(p => ({
        ...p,
        y: p.y + p.speedY
      })).filter(p => p.y < 110));

      // Spawn power-ups occasionally
      if (Math.random() < 0.002 && powerUps.length < 2) {
        setPowerUps(prev => [...prev, createPowerUp()]);
      }

      // Check if active power-up expired
      if (activePowerUp && Date.now() >= activePowerUp.expiresAt) {
        setActivePowerUp(null);
      }

      setVehicles(prevVehicles => prevVehicles.map(vehicle => {
        const speedMultiplier = level >= 6 ? 1.2 : 1;
        
        const weatherMultiplier = weatherEffect === 'rain' ? 0.9 : (weatherEffect === 'storm' ? 0.8 : 1);
        
        // Time freeze power-up slows all vehicles
        const freezeMultiplier = activePowerUp?.type === 'timeFreeze' && Date.now() < activePowerUp.expiresAt ? 0.3 : 1;
        
        if (vehicle.type === 'truck' || vehicle.type === 'construction') {
          return {
            ...vehicle,
            y: vehicle.y + vehicle.speedY * speedMultiplier * weatherMultiplier * freezeMultiplier,
          };
        } else if (vehicle.type === 'boss') {
          return {
            ...vehicle,
            x: vehicle.x + Math.sin(currentTime / 1000) * 0.5 * freezeMultiplier,
            y: vehicle.y + vehicle.speedY * 0.7 * freezeMultiplier,
          };
        } else {
          return {
            ...vehicle,
            x: vehicle.x + vehicle.speedX * 0.5 * speedMultiplier * weatherMultiplier * freezeMultiplier,
            y: vehicle.y + vehicle.speedY * 0.5 * speedMultiplier * weatherMultiplier * freezeMultiplier,
          };
        }
      }).filter(vehicle => 
        (vehicle.type === 'truck' || vehicle.type === 'construction') 
          ? (vehicle.y >= -15 && vehicle.y <= 110) 
          : (vehicle.x >= -15 && vehicle.x <= 115 && vehicle.y >= -15 && vehicle.y <= 110)
      ));

      if (Math.random() < 0.01 || currentTime - lastCopCarTime > 3000) {
        let vehicleTypes = ['police', 'police', 'police'];
        
        if (level >= 2) vehicleTypes.push('taxi', 'taxi');
        if (level >= 3) vehicleTypes.push('car', 'car', 'truck');
        if (level >= 4) vehicleTypes.push('construction', 'construction');
        if (level >= 5) vehicleTypes.push('ambulance');
        if (level >= 6) vehicleTypes.push('highway', 'highway');
        if (level >= 7 && vipActive) vehicleTypes.push('vip');
        if (level >= 9) vehicleTypes.push('swat', 'swat');
        
        if (level === 10 && Math.random() < 0.05) {
          vehicleTypes.push('boss');
        }
        
        const randomType = vehicleTypes[Math.floor(Math.random() * vehicleTypes.length)];
        setVehicles(prev => [...prev, createVehicle(randomType)]);
        
        if (randomType === 'police' || randomType === 'highway' || randomType === 'swat') {
          setLastCopCarTime(currentTime);
        }
      }

      setExplosions(prev => prev.map(exp => ({ ...exp, age: exp.age + 1 })).filter(exp => exp.age < 60));

      const slipperyFactor = isRoadSlippery ? 1.2 : 1;
      const speedBoost = activePowerUp?.type === 'speed' && Date.now() < activePowerUp.expiresAt ? 1.5 : 1;
      
      setCarPosition(prev => ({
        x: Math.max(2.5, Math.min(prev.x + carVelocity.x * 0.5 * slipperyFactor * speedBoost, 97.5)),
        y: Math.max(2.5, Math.min(prev.y + carVelocity.y * 0.5 * slipperyFactor * speedBoost, 97.5))
      }));

      const frictionFactor = isRoadSlippery ? 0.98 : 0.94;
      
      setCarVelocity(prev => ({
        x: prev.x * frictionFactor,
        y: prev.y * frictionFactor + 0.12
      }));

      setConePosition(prev => {
        const newY = prev.y + 1;
        if (newY > 115) {
          let newPattern = { y: -15, side: Math.random() < 0.5 ? 'left' : 'right' };
          
          if (level >= 4 && constructionActive) {
            newPattern.side = prev.side === 'left' ? 'right' : 'left';
          }
          
          return newPattern;
        }
        return { ...prev, y: newY };
      });

      animationFrameId = requestAnimationFrame(gameLoop);
    };

    animationFrameId = requestAnimationFrame(gameLoop);

    return () => cancelAnimationFrame(animationFrameId);
  }, [gameState, checkCollisions, carVelocity, createVehicle, createPowerUp, level, carPosition, lastCopCarTime, isRoadSlippery, weatherEffect, constructionActive, vipActive, activePowerUp, powerUps.length]);

  useEffect(() => {
    if (gameState !== 'playing') return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          endGame();
          return 0;
        }
        return prev - 1;
      });
      setTotalPlayTime(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [gameState, endGame]);

  const handleMouseMove = (e) => {
    if (gameState !== 'playing') return;
    const gameContainer = gameContainerRef.current;
    const containerRect = gameContainer.getBoundingClientRect();
    const relativeX = ((e.clientX - containerRect.left) / containerRect.width) * 100;
    const relativeY = ((e.clientY - containerRect.top) / containerRect.height) * 100;

    setCarPosition(prev => {
      const newX = Math.max(2.5, Math.min(relativeX, 97.5));
      if (newX > prev.x) {
        setCarDirection('right');
      } else if (newX < prev.x) {
        setCarDirection('left');
      }
      return {
        x: newX,
        y: Math.max(2.5, Math.min(relativeY, 97.5))
      };
    });

    setCarVelocity({ x: 0, y: 0 });
  };

  const [touchCenter, setTouchCenter] = useState(null);
  const [touchCurrent, setTouchCurrent] = useState(null);

  const handleTouchStart = (e) => {
    e.preventDefault();
    if (settings.controlType === 'joystick') {
      const touch = e.touches[0];
      const gameContainer = gameContainerRef.current;
      const containerRect = gameContainer.getBoundingClientRect();
      setTouchCenter({
        x: touch.clientX - containerRect.left,
        y: touch.clientY - containerRect.top
      });
      setTouchCurrent(null);
    } else {
      const touch = e.touches[0];
      const gameContainer = gameContainerRef.current;
      const containerRect = gameContainer.getBoundingClientRect();
      const relativeX = ((touch.clientX - containerRect.left) / containerRect.width) * 100;
      const relativeY = ((touch.clientY - containerRect.top) / containerRect.height) * 100;
      setCarPosition({
        x: Math.max(2.5, Math.min(relativeX, 97.5)),
        y: Math.max(2.5, Math.min(relativeY, 97.5))
      });
    }
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    if (settings.controlType === 'joystick') {
      if (!touchCenter) return;
      const touch = e.touches[0];
      const gameContainer = gameContainerRef.current;
      const containerRect = gameContainer.getBoundingClientRect();
      setTouchCurrent({
        x: touch.clientX - containerRect.left,
        y: touch.clientY - containerRect.top
      });
    } else {
      const touch = e.touches[0];
      const gameContainer = gameContainerRef.current;
      const containerRect = gameContainer.getBoundingClientRect();
      const relativeX = ((touch.clientX - containerRect.left) / containerRect.width) * 100;
      const relativeY = ((touch.clientY - containerRect.top) / containerRect.height) * 100;
      setCarPosition(prev => {
        const newX = Math.max(2.5, Math.min(relativeX, 97.5));
        if (newX > prev.x) {
          setCarDirection('right');
        } else if (newX < prev.x) {
          setCarDirection('left');
        }
        return {
          x: newX,
          y: Math.max(2.5, Math.min(relativeY, 97.5))
        };
      });
    }
  };

  const handleTouchEnd = () => {
    if (settings.controlType === 'joystick') {
      setTouchCenter(null);
      setTouchCurrent(null);
    }
  };

  useEffect(() => {
    if (gameState !== 'playing' || settings.controlType !== 'joystick' || !touchCenter || !touchCurrent) return;

    const moveCar = () => {
      const deltaX = touchCurrent.x - touchCenter.x;
      const deltaY = touchCurrent.y - touchCenter.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      const maxDistance = 50 * (settings.sensitivity / 50);
      const speed = Math.min(distance / maxDistance, 1) * 2 * (settings.responsiveness / 50);

      setCarPosition(prev => ({
        x: Math.max(2.5, Math.min(prev.x + (deltaX / maxDistance) * speed, 97.5)),
        y: Math.max(2.5, Math.min(prev.y + (deltaY / maxDistance) * speed, 97.5))
      }));

      setCarDirection(deltaX > 0 ? 'right' : 'left');
    };

    const animationId = requestAnimationFrame(moveCar);
    return () => cancelAnimationFrame(animationId);
  }, [gameState, touchCenter, touchCurrent, settings]);

  const getVehicleEmoji = (type) => {
    switch (type) {
      case 'police': return '🚓';
      case 'taxi': return '🚕';
      case 'car': return '🚙';
      case 'truck': return '🚚';
      case 'construction': return '🚜';
      case 'ambulance': return '🚑';
      case 'highway': return '🚔';
      case 'vip': return '🚘';
      case 'swat': return '🚐';
      case 'boss': return '🏎️';
      default: return '🚗';
    }
  };

  const levelColor = LEVEL_CONFIG[level]?.color || '#00ff88';

  return (
    <div
      className={`game-container ${weatherEffect ? weatherEffect : ''} ${
        darknessLevel > 0 ? 'night-mode' : ''
      } ${isRoadSlippery ? 'slippery' : ''}`}
      style={{ '--darkness-level': darknessLevel, '--level-color': levelColor }}
      onMouseMove={handleMouseMove}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      ref={gameContainerRef}
    >
      {gameState === 'start' && !showSettings && (
        <div className="start-screen">
          <div className="logo-container">
            <h1 className="game-title">ROAD<span className="rage">RAGE</span></h1>
            <div className="title-underline"></div>
          </div>
          
          <div className="how-to-play">
            <div className="instruction">
              <span className="emoji-large">🚗</span>
              <span className="plus">+</span>
              <span className="emoji-large">🚓</span>
              <span className="equals">=</span>
              <span className="reward">💥 +10 pts +3s</span>
            </div>
            <p className="tip">Smash cop cars, avoid civilians!</p>
          </div>

          {highScores.length > 0 && (
            <div className="high-scores">
              <h3>🏆 HIGH SCORES</h3>
              <ol>
                {highScores.slice(0, 3).map((hs, i) => (
                  <li key={i}>{hs.score} pts</li>
                ))}
              </ol>
            </div>
          )}

          <div className="level-preview">
            <h3>🎮 10 LEVELS OF CHAOS</h3>
            <div className="levels-grid">
              {Object.entries(LEVEL_CONFIG).map(([lvl, config]) => (
                <div key={lvl} className="level-item" style={{ '--level-accent': config.color }}>
                  <span className="level-num">{lvl}</span>
                  <span className="level-name">{config.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="button-group">
            <button className="primary-btn" onClick={startGame} onTouchStart={startGame}>
              <span className="btn-text">START ENGINE</span>
              <span className="btn-icon">🏁</span>
            </button>
            <button className="secondary-btn" onClick={() => setShowSettings(true)}>
              ⚙️ Settings
            </button>
          </div>

          <button 
            className="sound-toggle"
            onClick={() => setSoundEnabled(!soundEnabled)}
          >
            {soundEnabled ? '🔊' : '🔇'}
          </button>
        </div>
      )}

      {showSettings && (
        <div className="settings-screen">
          <h2>⚙️ SETTINGS</h2>
          <div className="settings-group">
            <label>
              <span className="setting-label">Control Type</span>
              <select
                value={settings.controlType}
                onChange={(e) => setSettings({...settings, controlType: e.target.value})}
              >
                <option value="joystick">🕹️ Joystick</option>
                <option value="touch">👆 Direct Touch</option>
              </select>
            </label>
          </div>
          <div className="settings-group">
            <label>
              <span className="setting-label">Sensitivity: {settings.sensitivity}%</span>
              <input
                type="range"
                min="1"
                max="100"
                value={settings.sensitivity}
                onChange={(e) => setSettings({...settings, sensitivity: parseInt(e.target.value)})}
              />
            </label>
          </div>
          <div className="settings-group">
            <label>
              <span className="setting-label">Responsiveness: {settings.responsiveness}%</span>
              <input
                type="range"
                min="1"
                max="100"
                value={settings.responsiveness}
                onChange={(e) => setSettings({...settings, responsiveness: parseInt(e.target.value)})}
              />
            </label>
          </div>
          <div className="settings-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={soundEnabled}
                onChange={(e) => setSoundEnabled(e.target.checked)}
              />
              <span className="setting-label">🔊 Sound Effects</span>
            </label>
          </div>
          <button className="primary-btn" onClick={() => setShowSettings(false)}>
            ✓ Save & Close
          </button>
        </div>
      )}

      {gameState === 'playing' && (
        <>
          <div className="game-info">
            <div className="score-indicator">
              <span className="label">SCORE</span>
              <span className="value">{score}</span>
              {scoreNotification && (
                <div className={`notification score-notification ${scoreNotification.startsWith('-') ? 'negative' : ''}`}>
                  {scoreNotification}
                </div>
              )}
            </div>
            
            <div className={`level-indicator ${levelUpNotification ? 'level-up' : ''}`}>
              <span className="label">LEVEL {level}</span>
              <span className="level-name">{LEVEL_CONFIG[level].name}</span>
              {levelUpNotification && <div className="level-up-notification">🎉 LEVEL UP!</div>}
            </div>
            
            <div className={`timer-indicator ${timeLeft <= 5 ? 'critical' : ''}`}>
              <span className="label">TIME</span>
              <span className="value">{timeLeft}s</span>
              {timeNotification && (
                <div className={`notification time-notification ${timeNotification.startsWith('-') ? 'negative' : ''}`}>
                  {timeNotification}
                </div>
              )}
            </div>
          </div>

          {combo >= 2 && (
            <div className="combo-display" style={{ '--combo-scale': 1 + (combo * 0.1) }}>
              <span className="combo-count">{combo}x</span>
              <span className="combo-label">COMBO!</span>
            </div>
          )}

          {activePowerUp && Date.now() < activePowerUp.expiresAt && (
            <div 
              className="active-powerup"
              style={{ '--powerup-color': POWER_UPS[activePowerUp.type].color }}
            >
              <span className="powerup-emoji">{POWER_UPS[activePowerUp.type].emoji}</span>
              <div 
                className="powerup-timer"
                style={{ 
                  '--remaining': (activePowerUp.expiresAt - Date.now()) / POWER_UPS[activePowerUp.type].duration 
                }}
              ></div>
            </div>
          )}
          
          {weatherEffect === 'rain' && (
            <div className="rain-overlay"></div>
          )}
          
          {weatherEffect === 'storm' && (
            <>
              <div className="rain-overlay heavy"></div>
              <div className="lightning-flash"></div>
            </>
          )}
          
          <div className="road">
            <div className="road-lines"></div>
            
            {/* Power-ups */}
            {powerUps.map(powerUp => (
              <div
                key={powerUp.id}
                className="powerup"
                style={{ 
                  left: `${powerUp.x}%`, 
                  top: `${powerUp.y}%`,
                  '--powerup-color': POWER_UPS[powerUp.type].color
                }}
              >
                {POWER_UPS[powerUp.type].emoji}
              </div>
            ))}
            
            <div 
              className={`car facing-${carDirection} ${spinDirection ? `spinning-${spinDirection}` : ''} ${activePowerUp?.type === 'shield' ? 'shielded' : ''} ${activePowerUp?.type === 'speed' ? 'boosted' : ''}`}
              style={{ left: `${carPosition.x}%`, top: `${carPosition.y}%` }}
            >
              🚗
              {activePowerUp?.type === 'shield' && <div className="shield-effect">🛡️</div>}
            </div>
            
            <div className="center-line"></div>
            
            {vehicles.map((vehicle) => (
              !vehicle.exploded && (
                <div key={vehicle.id}
                  className={`vehicle ${vehicle.type} ${vehicle.speedX >= 0 ? 'facing-right' : 'facing-left'} ${vehicle.flashing ? 'flashing' : ''} ${vehicle.isVIP ? 'vip-vehicle' : ''} ${vehicle.isBoss ? 'boss-vehicle' : ''}`}
                  style={{ left: `${vehicle.x}%`, top: `${vehicle.y}%` }}>
                  {getVehicleEmoji(vehicle.type)}
                  {vehicle.isBoss && vehicle.health && (
                    <div className="boss-health">{vehicle.health}</div>
                  )}
                </div>
              )
            ))}
            
            {explosions.map((explosion, index) => (
              <React.Fragment key={index}>
                {explosion.type === 'collision' ? (
                  <div
                    className="collision-marker"
                    style={{ 
                      left: `${explosion.x}%`, 
                      top: `${explosion.y}%`,
                      opacity: 1 - explosion.age / 60
                    }}>
                    <div className="collision-x">💥</div>
                    <div className="collision-penalty">
                      <span className="penalty-score">{explosion.points || '-10'} pts</span>
                      <span className="penalty-time">{explosion.time || '-3'} sec</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div
                      className="explosion-notification"
                      style={{ 
                        left: `${explosion.x}%`, 
                        top: `${explosion.y - 5}%`,
                        opacity: 1 - explosion.age / 60
                      }}>
                      <span className="score-gain">{explosion.points || '+10'} pts</span>
                      <span className="time-gain">{explosion.time || '+3'} sec</span>
                    </div>
                    <div
                      className="explosion"
                      style={{ 
                        left: `${explosion.x}%`, 
                        top: `${explosion.y}%`,
                        opacity: 1 - explosion.age / 60
                      }}>
                      💥
                    </div>
                  </>
                )}
              </React.Fragment>
            ))}
            
            <div className={`cone-group ${constructionActive ? 'construction-zone' : ''}`} style={{ 
              top: `${conePosition.y}%`, 
              left: conePosition.side === 'left' ? '2%' : '62%', 
              width: '30%' 
            }}>
              <div className="pothole" style={{ 
                left: conePosition.side === 'left' ? '29%' : '30%', 
                top: '0' 
              }}>🕳️</div>
              <div className="cone">🚧</div>
              <div className="cone">🚧</div>
              {constructionActive && (
                <>
                  <div className="barrier">🚧</div>
                  <div className="barrier">🚧</div>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {gameState === 'over' && (
        <div className="game-over-screen">
          <h1 className="game-over-title">GAME OVER</h1>
          
          <div className="final-stats">
            <div className="stat-row main-stat">
              <span className="stat-label">FINAL SCORE</span>
              <span className="stat-value">{score}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Level Reached</span>
              <span className="stat-value">{level} - {LEVEL_CONFIG[level].name}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Max Combo</span>
              <span className="stat-value">{maxCombo}x</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Play Time</span>
              <span className="stat-value">{Math.floor(totalPlayTime / 60)}m {totalPlayTime % 60}s</span>
            </div>
          </div>

          {highScores.length > 0 && score >= highScores[0].score && (
            <div className="new-high-score">🎉 NEW HIGH SCORE! 🎉</div>
          )}

          <div className="play-again-container">
            <button 
              className="primary-btn"
              onClick={(e) => {
                e.preventDefault();
                startGame();
              }} 
              onTouchStart={(e) => {
                e.preventDefault();
                startGame();
              }}
            >
              <span className="btn-text">PLAY AGAIN</span>
              <span className="btn-icon">🔄</span>
            </button>
            <button 
              className="secondary-btn"
              onClick={(e) => {
                e.preventDefault();
                setGameState('start');
              }}
            >
              ← Main Menu
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
